import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import subscriptionApi from '../../api/subscription';
import { logout } from './authSlice';

const initialState = {
  plans: [],
  isLoading: false,
  isSuccess: false,
  error: null,
  clientDetails: null,
  clientDetailsLoading: false,
  clientDetailsError: null,
  billingSummary: null,
  billingLoading: false,
  billingError: null
};

export const fetchSubscriptionPlans = createAsyncThunk(
  'subscription/fetchPlans',
  async (_, thunkApi) => {
    try {
      const state = thunkApi.getState();
      const token = state.auth.token;
      const response = await subscriptionApi.getSubscriptionPlans(token);
      // console.log("Fetched Plans:", response);
      // if API returns array, return as is
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.plans)) return response.plans;
      return [];
    } catch (err) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
      return thunkApi.rejectWithValue(backendMsg || err?.message || 'Failed to fetch plans');
    }
  }
);

export const fetchClientDetailsThunk = createAsyncThunk(
  'subscription/fetchClientDetails',
  async (_, thunkApi) => {
    try {
      const state = thunkApi.getState();
      const token = state.auth.token;
      const response = await subscriptionApi.fetchClientDetails(token);
      // console.log("Fetched Client Details:", response);
      return response;
    } catch (err) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
      return thunkApi.rejectWithValue(backendMsg || err?.message || 'Failed to fetch client details');
    }
  }
);

export const fetchBillingSummary = createAsyncThunk(
  'subscription/fetchBillingSummary',
  async (arg, thunkApi) => {
    const force = arg && arg.force;
    const state = thunkApi.getState();
    if (!force && state.subscription.billingSummary) {
      // Already have billing summary, don't fetch again
      return state.subscription.billingSummary;
    }
    const token = state.auth.token;
    try {
      const response = await subscriptionApi.getBillingSummary(token);
      return response;
    } catch (err) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
      return thunkApi.rejectWithValue(backendMsg || err?.message || 'Failed to fetch billing summary');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearPlans: (state) => {
      state.plans = [];
      state.isLoading = false;
      state.isSuccess = false;
      state.error = null;
      state.clientDetails = null;
      state.clientDetailsLoading = false;
      state.clientDetailsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Defensive: always set plans as array
        let plans = Array.isArray(action.payload) ? action.payload : [];
        // Sort: non-custom by price ascending, custom at end
        plans = [
          ...plans.filter(p => !p.is_custom).sort((a, b) => {
            const aPrice = Number(a.base_price) || 0;
            const bPrice = Number(b.base_price) || 0;
            return aPrice - bPrice;
          }),
          ...plans.filter(p => p.is_custom)
        ];
        state.plans = plans;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.error = action.payload;
      })
      .addCase(fetchClientDetailsThunk.pending, (state) => {
        state.clientDetailsLoading = true;
        state.clientDetailsError = null;
      })
      .addCase(fetchClientDetailsThunk.fulfilled, (state, action) => {
        state.clientDetailsLoading = false;
        state.clientDetails = action.payload;
        state.clientDetailsError = null;
      })
      .addCase(fetchClientDetailsThunk.rejected, (state, action) => {
        state.clientDetailsLoading = false;
        state.clientDetailsError = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.plans = [];
        state.isLoading = false;
        state.isSuccess = false;
        state.error = null;
        state.clientDetails = null;
        state.clientDetailsLoading = false;
        state.clientDetailsError = null;
      })
      .addCase(fetchBillingSummary.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(fetchBillingSummary.fulfilled, (state, action) => {
        state.billingLoading = false;
        state.billingSummary = action.payload;
        state.billingError = null;
      })
      .addCase(fetchBillingSummary.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload;
        state.billingSummary = null;
      });
  }
});

// Selectors
export const selectSubscriptionPlans = (state) => Array.isArray(state.subscription.plans) ? state.subscription.plans : [];
export const selectSubscriptionLoading = (state) => state.subscription.isLoading;
export const selectSubscriptionError = (state) => state.subscription.error;

// Filtered selectors
export const selectRecurringPlans = createSelector([selectSubscriptionPlans],(plans) => plans.filter(plan => plan.type === 'subscription'));
export const selectOneTimePlans = createSelector([selectSubscriptionPlans],(plans) => plans.filter(plan => plan.type === 'one_time'));

export const selectClientDetails = (state) => state.subscription.clientDetails;
export const selectClientDetailsLoading = (state) => state.subscription.clientDetailsLoading;
export const selectClientDetailsError = (state) => state.subscription.clientDetailsError;

export const selectBillingSummary = (state) => state.subscription.billingSummary;
export const selectBillingLoading = (state) => state.subscription.billingLoading;
export const selectBillingError = (state) => state.subscription.billingError;

export const { clearPlans } = subscriptionSlice.actions;

export default subscriptionSlice.reducer; 